import { useCallback, useEffect, useRef, useState } from 'react';
import { loadScenarioHistory, redoScenarioHistory, undoScenarioHistory } from '../data/repository';
import type { ScenarioHistoryStateRecord, ScenarioSnapshot } from '../data/types';
import type { Table, Wall } from '../types';

export interface HistoryEntry {
  id?: string;
  tables: Table[];
  walls: Wall[];
  description: string;
}

function cloneSnapshot<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function createHistoryEntry(nextTables: Table[], nextWalls: Wall[], description: string = '', id?: string): HistoryEntry {
  return {
    id,
    tables: cloneSnapshot(nextTables),
    walls: cloneSnapshot(nextWalls),
    description,
  };
}

function serializeSnapshot(entry: Pick<HistoryEntry, 'tables' | 'walls'>): string {
  return JSON.stringify({
    tables: entry.tables,
    walls: entry.walls,
  });
}

function areSnapshotsEqual(
  left: Pick<HistoryEntry, 'tables' | 'walls'> | null | undefined,
  right: Pick<HistoryEntry, 'tables' | 'walls'> | null | undefined,
): boolean {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return serializeSnapshot(left) === serializeSnapshot(right);
}

function mapHistoryState(state: ScenarioHistoryStateRecord): {
  history: HistoryEntry[];
  historyIndex: number;
} {
  return {
    history: state.entries.map((entry) => createHistoryEntry(entry.tables, entry.walls, entry.description, entry.id)),
    historyIndex: state.cursorIndex,
  };
}

export function useHistory(
  scenarioName: string | null,
  _deprecatedOnAutoSave?: (nextTables: Table[], nextWalls: Wall[]) => void,
  maxEntries: number = 20,
): {
  history: HistoryEntry[];
  historyIndex: number;
  historyReady: boolean;
  lastActionMessage: string;
  pushHistory: (nextTables: Table[], nextWalls: Wall[], description?: string) => void;
  syncHistoryToSnapshot: (nextTables: Table[], nextWalls: Wall[], description?: string) => void;
  undo: () => Promise<ScenarioSnapshot | null>;
  redo: () => Promise<ScenarioSnapshot | null>;
  setLastActionMessage: (msg: string) => void;
} {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);
  const [historyReady, setHistoryReady] = useState<boolean>(false);
  const [lastActionMessage, setLastActionMessage] = useState<string>('');
  const historyRef = useRef<HistoryEntry[]>([]);
  const historyIndexRef = useRef<number>(-1);

  const commitHistory = useCallback((nextHistory: HistoryEntry[], nextIndex: number): void => {
    historyRef.current = nextHistory;
    historyIndexRef.current = nextIndex;
    setHistory(nextHistory);
    setHistoryIndex(nextIndex);
    setHistoryReady(true);
  }, []);

  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  useEffect(() => {
    historyIndexRef.current = historyIndex;
  }, [historyIndex]);

  useEffect(() => {
    let cancelled = false;

    async function loadHistory() {
      if (!scenarioName) {
        commitHistory([], -1);
        return;
      }

      setHistoryReady(false);
      try {
        const state = await loadScenarioHistory(scenarioName);
        if (cancelled) {
          return;
        }

        const mapped = mapHistoryState(state);
        commitHistory(mapped.history, mapped.historyIndex);
      } catch (error) {
        if (!cancelled) {
          commitHistory([], -1);
        }
      }
    }

    void loadHistory();

    return () => {
      cancelled = true;
    };
  }, [commitHistory, scenarioName]);

  const pushHistory = useCallback((nextTables: Table[], nextWalls: Wall[], description: string = ''): void => {
    const snapshot = createHistoryEntry(nextTables, nextWalls, description);
    const truncated = historyRef.current.slice(0, historyIndexRef.current + 1);
    const currentEntry = truncated[truncated.length - 1];
    if (currentEntry && areSnapshotsEqual(currentEntry, snapshot)) {
      return;
    }

    let nextHistory = [...truncated, snapshot];
    if (nextHistory.length > maxEntries) {
      nextHistory = nextHistory.slice(nextHistory.length - maxEntries);
    }
    commitHistory(nextHistory, nextHistory.length - 1);
  }, [commitHistory, maxEntries]);

  const syncHistoryToSnapshot = useCallback((nextTables: Table[], nextWalls: Wall[], description: string = ''): void => {
    const snapshot = createHistoryEntry(nextTables, nextWalls, description);
    const currentHistory = historyRef.current;

    if (currentHistory.length === 0) {
      commitHistory([snapshot], 0);
      return;
    }

    const currentEntry = currentHistory[historyIndexRef.current];
    if (currentEntry && areSnapshotsEqual(currentEntry, snapshot)) {
      return;
    }

    const matchingIndex = currentHistory.findIndex((entry) => areSnapshotsEqual(entry, snapshot));
    if (matchingIndex >= 0) {
      historyIndexRef.current = matchingIndex;
      setHistoryIndex(matchingIndex);
      setHistoryReady(true);
    }
  }, [commitHistory]);

  const undo = useCallback(async (): Promise<ScenarioSnapshot | null> => {
    if (!scenarioName || historyIndexRef.current <= 0) {
      return null;
    }

    const result = await undoScenarioHistory(scenarioName);
    const mapped = mapHistoryState(result.history);
    commitHistory(mapped.history, mapped.historyIndex);
    const entry = mapped.history[mapped.historyIndex];
    setLastActionMessage(entry?.description ? `Undid: ${entry.description}` : 'Undid last action');
    return result.scenario;
  }, [commitHistory, scenarioName]);

  const redo = useCallback(async (): Promise<ScenarioSnapshot | null> => {
    if (!scenarioName || historyIndexRef.current >= historyRef.current.length - 1) {
      return null;
    }

    const result = await redoScenarioHistory(scenarioName);
    const mapped = mapHistoryState(result.history);
    commitHistory(mapped.history, mapped.historyIndex);
    const entry = mapped.history[mapped.historyIndex];
    setLastActionMessage(entry?.description ? `Redid: ${entry.description}` : 'Redid last action');
    return result.scenario;
  }, [commitHistory, scenarioName]);

  return {
    history,
    historyIndex,
    historyReady,
    lastActionMessage,
    pushHistory,
    syncHistoryToSnapshot,
    undo,
    redo,
    setLastActionMessage,
  };
}
